/*
 * <color-swatch-picker>
 *
 * Each garment colour is a separate product at its own URL. The swatches are
 * real <a> links, so without JS (or on modified clicks) they navigate normally.
 *
 * With JS, a plain click swaps colours in place instead of reloading the page:
 * it fetches the target product's main section via the Section Rendering API and
 * replaces only the parts that differ — the media gallery, title, price, and the
 * picker's own active state. The course-designer form and its generated overlay
 * live outside the media gallery, so an in-progress design is preserved.
 */
if (!customElements.get('color-swatch-picker')) {
  customElements.define(
    'color-swatch-picker',
    class ColorSwatchPicker extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.addEventListener('click', this.onClick.bind(this));
        this._onPop = this.onPop.bind(this);
        window.addEventListener('popstate', this._onPop);
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this._onPop);
      }

      onClick(event) {
        // Leave modified clicks (new tab/window) and non-primary buttons alone.
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        const link = event.target.closest('a.color-swatch');
        if (!link || !this.contains(link)) return;

        event.preventDefault();
        if (link.getAttribute('aria-current') === 'page') return;
        this.navigate(link.href, true);
      }

      onPop() {
        // Re-render whatever colour the new history entry points at.
        this.navigate(window.location.href, false);
      }

      async navigate(url, push) {
        const gallery = document.getElementById('MediaGallery-' + this.sectionId);
        if (gallery) gallery.classList.add('is-color-swapping');

        try {
          const response = await fetch(this.sectionUrl(url));
          if (!response.ok) throw new Error('HTTP ' + response.status);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          await this.preloadImages(doc);
          const release = this.holdPosition();
          if (!this.swap(doc)) throw new Error('Expected nodes missing from response');
          release();
          if (push) history.pushState({ colorSwatch: true }, '', url);
        } catch (error) {
          // Anything unexpected: fall back to a normal full-page navigation.
          window.location.href = url;
        }
      }

      // Fetch the new colour's photos before anything is swapped, so the
      // gallery and the design dock change straight from one garment to the
      // other instead of passing through empty frames. The parsed document is
      // inert and never loads its own images, so copies are decoded here and
      // the real ones come out of the cache. Capped, so a slow photo only costs
      // the old behaviour.
      async preloadImages(doc) {
        const gallery = doc.getElementById('MediaGallery-' + this.sectionId);
        if (!gallery) return;
        const imgs = Array.from(gallery.querySelectorAll('.course-dock__slide img'));
        const first = gallery.querySelector('.product__media img');
        if (first) imgs.push(first);
        const loads = imgs.map((img) => {
          const copy = new Image();
          if (img.getAttribute('sizes')) copy.sizes = img.getAttribute('sizes');
          if (img.getAttribute('srcset')) copy.srcset = img.getAttribute('srcset');
          copy.src = img.getAttribute('src');
          return copy.decode().catch(() => {});
        });
        await Promise.race([Promise.all(loads), new Promise((resolve) => setTimeout(resolve, 1500))]);
      }

      // The design dock (the preview strip pinned to the top of a phone) lives
      // inside the gallery, so replacing the gallery used to replace the dock:
      // it slid away and back in on every colour change. The dock element
      // stays instead, moved into the new gallery, and only its slides are
      // exchanged for the new colour's. Returns a function that puts the
      // strip's scroll position back once the new gallery is in the page.
      keepDock(currentGallery, nextGallery) {
        const oldDock = currentGallery.querySelector('[data-ref="dock"]');
        const newDock = nextGallery.querySelector('[data-ref="dock"]');
        if (!oldDock || !newDock) return () => {};
        const oldTrack = oldDock.querySelector('[data-ref="dock-track"]');
        const newTrack = newDock.querySelector('[data-ref="dock-track"]');
        if (!oldTrack || !newTrack) return () => {};
        const left = oldTrack.scrollLeft;
        oldTrack.replaceChildren(...Array.from(newTrack.childNodes));
        newDock.replaceWith(oldDock);
        return () => {
          oldTrack.scrollLeft = left;
        };
      }

      // Keep the swatches where the customer's finger left them. The swap drops
      // in a gallery whose photos haven't loaded yet, so for a moment it is
      // shorter than the one it replaced, and on a phone it sits above the
      // picker; iOS Safari has no scroll anchoring, so the page jumped by the
      // difference. The new gallery is held at the old height until its first
      // photo is in, and the picker is pinned to its spot on screen meanwhile.
      // Any scroll of the customer's own ends the pin.
      holdPosition() {
        const startTop = this.getBoundingClientRect().top;
        const oldGallery = document.getElementById('MediaGallery-' + this.sectionId);
        const oldHeight = oldGallery ? oldGallery.offsetHeight : 0;
        let frame = null;
        let done = false;

        const stop = () => {
          if (done) return;
          done = true;
          cancelAnimationFrame(frame);
          ['touchstart', 'wheel', 'keydown'].forEach((type) => window.removeEventListener(type, stop));
          const gallery = document.getElementById('MediaGallery-' + this.sectionId);
          if (gallery) gallery.style.minHeight = '';
        };
        ['touchstart', 'wheel', 'keydown'].forEach((type) =>
          window.addEventListener(type, stop, { passive: true, once: true })
        );

        const pin = () => {
          const drift = this.getBoundingClientRect().top - startTop;
          if (Math.abs(drift) > 1) window.scrollBy(0, drift);
        };

        return () => {
          const gallery = document.getElementById('MediaGallery-' + this.sectionId);
          if (gallery && oldHeight) gallery.style.minHeight = oldHeight + 'px';
          pin();

          const until = performance.now() + 1500;
          const tick = () => {
            if (done) return;
            pin();
            if (performance.now() < until) frame = requestAnimationFrame(tick);
            else stop();
          };
          frame = requestAnimationFrame(tick);

          const firstImage = gallery && gallery.querySelector('.product__media img');
          if (firstImage && !firstImage.complete) {
            firstImage.addEventListener(
              'load',
              () => {
                if (gallery) gallery.style.minHeight = '';
                requestAnimationFrame(pin);
              },
              { once: true }
            );
          } else if (gallery) {
            gallery.style.minHeight = '';
          }
        };
      }

      sectionUrl(url) {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('section_id', this.sectionId);
        return parsed.toString();
      }

      swap(doc) {
        const sid = this.sectionId;

        const currentGallery = document.getElementById('MediaGallery-' + sid);
        const nextGallery = doc.getElementById('MediaGallery-' + sid);
        const nextPicker = doc.querySelector('color-swatch-picker');
        if (!currentGallery || !nextGallery || !nextPicker) return false;

        // Validate the buy buttons up front so we never half-apply a swap: if
        // availability differs between colours (one renders no <product-form>),
        // bail before mutating and let the caller fall back to a full load.
        const currentForm = document.querySelector('product-form');
        const nextForm = doc.querySelector('product-form');
        if (Boolean(currentForm) !== Boolean(nextForm)) return false;

        // Photos for the new colour — the course overlay is a separate sibling
        // and is intentionally left in place.
        const restoreDockScroll = this.keepDock(currentGallery, nextGallery);
        currentGallery.replaceWith(nextGallery);
        restoreDockScroll();

        // Picker active state + selected-colour label.
        this.innerHTML = nextPicker.innerHTML;

        // Title block (name + the duplicate title link).
        const currentTitle = document.querySelector('#ProductInfo-' + sid + ' .product__title');
        const nextTitle = doc.querySelector('#ProductInfo-' + sid + ' .product__title');
        if (currentTitle && nextTitle) currentTitle.innerHTML = nextTitle.innerHTML;

        // Price.
        const currentPrice = document.getElementById('price-' + sid);
        const nextPrice = doc.getElementById('price-' + sid);
        if (currentPrice && nextPrice) currentPrice.innerHTML = nextPrice.innerHTML;

        // Buy buttons — the hidden variant id and dynamic-checkout button must
        // match the new colour, or the cart gets the wrong product. The course
        // designer's line-item inputs are bound to the form by its (constant)
        // id, so they re-attach to the swapped-in form automatically.
        if (currentForm && nextForm) currentForm.replaceWith(nextForm);

        // Variant picker — its option inputs and embedded selected-variant JSON
        // belong to the previous colour's product. Each colour is a separate
        // product, so a size change is handled by <product-info>, which fetches
        // variants from <product-info data-url>. Leaving the old picker (and the
        // stale data-url) in place means a later size change would resolve to the
        // previous colour and add the wrong product to the cart. Replacing the
        // element re-runs its connectedCallback so the change listener re-binds.
        const currentVariants = document.getElementById('variant-selects-' + sid);
        const nextVariants = doc.getElementById('variant-selects-' + sid);
        // Remember the size the customer had picked before we drop in the new
        // colour's default-rendered picker, so it survives the swap (mirrors how
        // the course design persists). Each colour is a separate product with its
        // own option-value ids, so match by the human-readable value (e.g. "M").
        const selectedOptionValues = currentVariants ? this.readSelectedOptionValues(currentVariants) : [];
        if (currentVariants && nextVariants) currentVariants.replaceWith(nextVariants);

        // Re-point <product-info> at the new colour's product so subsequent
        // variant (size) changes fetch from the correct URL.
        const currentInfo = document.getElementById('MainProduct-' + sid);
        const nextInfo = doc.getElementById('MainProduct-' + sid);
        if (currentInfo && nextInfo) {
          if (nextInfo.dataset.url) currentInfo.dataset.url = nextInfo.dataset.url;
          if (nextInfo.dataset.productId) currentInfo.dataset.productId = nextInfo.dataset.productId;
        }

        // Re-apply the remembered size now that data-url points at the new colour.
        // Re-checking the matching input and dispatching `change` lets
        // <product-info> resolve the right variant id and price for this colour.
        if (nextVariants) this.applySelectedOptionValues(nextVariants, selectedOptionValues);

        // Description — each colour is its own product, so the copy can differ.
        // The block always renders (hidden when blank), so both nodes exist and
        // the hidden state carries across with the content.
        const currentDescription = document.getElementById('ProductDescription-' + sid);
        const nextDescription = doc.getElementById('ProductDescription-' + sid);
        if (currentDescription && nextDescription) {
          currentDescription.innerHTML = nextDescription.innerHTML;
          currentDescription.toggleAttribute('hidden', nextDescription.hasAttribute('hidden'));
        }

        // Share URL — the input holds the previous colour's product URL.
        const currentShare = document.getElementById('ShareUrl-' + sid);
        const nextShare = doc.getElementById('ShareUrl-' + sid);
        if (currentShare && nextShare) currentShare.value = nextShare.value;

        const heading = (nextTitle && nextTitle.querySelector('h1')) || doc.querySelector('h1');
        if (heading) document.title = heading.textContent.trim();

        // Let the course designer re-gate its (newly swapped) submit button.
        document.dispatchEvent(new CustomEvent('color-swatch:swapped', { detail: { sectionId: sid } }));

        return true;
      }

      // The selected value of each option group (Size, …) in DOM order. Both
      // colours render the same option layout, so order lines the groups up.
      readSelectedOptionValues(variantSelects) {
        return Array.from(variantSelects.querySelectorAll('.product-form__input')).map((group) => {
          const select = group.querySelector('select');
          if (select) return select.value;
          const checked = group.querySelector('input:checked');
          return checked ? checked.value : null;
        });
      }

      // Restore the remembered values onto a freshly swapped-in picker. Only acts
      // when the value differs from the new colour's default, and fires one
      // `change` so <product-info> fetches the matching variant for this colour.
      applySelectedOptionValues(variantSelects, values) {
        Array.from(variantSelects.querySelectorAll('.product-form__input')).forEach((group, i) => {
          const value = values[i];
          if (value == null) return;

          const select = group.querySelector('select');
          if (select) {
            if (select.value !== value && Array.from(select.options).some((o) => o.value === value)) {
              select.value = value;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return;
          }

          const input = Array.from(group.querySelectorAll('input[type="radio"]')).find((r) => r.value === value);
          if (input && !input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    }
  );
}
